import { observer } from 'mobx-react-lite'
import { Icon, Tooltip, Typography } from 'shared/ui'
import { type Integration } from 'entities/Integrations'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { useIntegrationStoreMemo } from 'pages/settings/pages/integrations/pages/integrationSettings/hooks/useIntegrationStoreMemo'
import { CollapseIntegrationSettingsTimeline } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/CollapseIntegrationSettingsTimeline/CollapseIntegrationSettingsTimeline'
import { CollapseAircallNumbers } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/ui/CollapseAircallNumbers/CollapseAircallNumbers'
import { AircallSettingItem } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/ui/AircallSettingItem/AircallSettingItem'
import { IntegrationAircallSettingsStore } from './store/IntegrationAircallSettingsStore'
import styles from './styles.module.scss'

type IIntegrationAircallSettingsProps = {
  integration: Integration
}

export const IntegrationAircallSettings = observer(
  ({ integration }: IIntegrationAircallSettingsProps) => {
    const entities = useEntitiesContext()
    const { collapseStore, timelineStore, aircallNumbersStore } = useIntegrationStoreMemo(
      IntegrationAircallSettingsStore,
      integration,
      entities
    )

    return (
      <div>
        <CollapseAircallNumbers
          collapseStore={collapseStore}
          aircallNumbersStore={aircallNumbersStore}
        />
        <CollapseIntegrationSettingsTimeline
          collapseStore={collapseStore}
          name={integration.name}
          dailyBundles={timelineStore.dailyBundles}
          labelPart={'call'}
          onDailyBundlesToggle={timelineStore.onDailyBundlesToggle}
        />
        <AircallSettingItem
          icon='phone'
          option='outboundCallsAircall'
          title='Open outbound calls via Aircall'
          description='This is an organization-wide setting that affects all outbound calls for all inboxes'
          onUpdate={(value) => entities.organizationStore.updateOrganizationCallViaAircall(value)}
        />
        <AircallSettingItem
          icon='messagePlus'
          option='createNewConvViaAircall'
          title={
            <Typography
              ariaLabel='autoCreateConversationsTitle'
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              className={styles.autoCreateConversationsTitle}
            >
              Auto-create conversations
              <Tooltip
                label={
                  <div>
                    In Salesmsg, a conversation will automatically <br /> start when someone calls
                    your Aircall number or <br /> when you make an outbound Aircall - if
                    there&apos;s
                    <br /> no existing conversation.
                  </div>
                }
                placement='top'
              >
                <Icon fontSize={14} icon={'question'} color={'var(--content-neutral-primary)'} />
              </Tooltip>
            </Typography>
          }
          description='Automatically start a conversation when you make or get a call through Aircall'
          onUpdate={(value) =>
            entities.organizationStore.updateOrganizationCallAutoCreateConversations(value)
          }
        />
      </div>
    )
  }
)
